import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Calendar, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const SIZES = ["1–10", "11–50", "51–200", "201–1 000", "1 000+"];
const ROLES = ["Project Manager", "CTO / VP Engineering", "CEO / Founder", "Operations Manager", "PMO Lead", "Team Lead", "Other"];

const inputClass = "w-full h-10 bg-white border border-gray-200 text-gray-900 text-sm px-3 rounded-lg outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-colors";
const labelClass = "block text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-1.5 font-semibold";

const CALENDLY_SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";

function loadCalendlyScript() {
  if (document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`)) return;
  const s = document.createElement("script");
  s.src = CALENDLY_SCRIPT_SRC;
  s.async = true;
  document.body.appendChild(s);
}

export default function BookDemoModal({ onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "",
    company_size: "", role: "", message: "",
  });
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [booking, setBooking] = useState(null);
  const [calendlyConfirmed, setCalendlyConfirmed] = useState(false);
  const [confirmedMeeting, setConfirmedMeeting] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Load Calendly's embed script once we actually need the widget.
  useEffect(() => {
    if (step === 2 && booking?.scheduling_url) loadCalendlyScript();
  }, [step, booking]);

  // Calendly posts a window message when the prospect confirms a slot. We
  // use it for two things: showing a "you're booked" state without leaving
  // the modal, and — since our Calendly plan doesn't support webhook
  // subscriptions — reporting the booking back to our own backend so sales
  // gets the confirmed time + meeting link synced into CRM records (see
  // POST /demo-bookings/{id}/confirm in routes/demo_bookings.py). If you
  // upgrade Calendly to Standard+ and register the webhook instead
  // (scripts/setup_calendly_webhook.py), that becomes the authoritative
  // sync and this call is just a harmless, faster duplicate.
  useEffect(() => {
    function onMessage(e) {
      if (typeof e.data !== "object" || !e.data) return;
      if (String(e.data.event || "").indexOf("calendly.") !== 0) return;
      if (e.data.event !== "calendly.event_scheduled") return;
      setCalendlyConfirmed(true);
      const eventUri = e.data.payload?.event?.uri;
      const inviteeUri = e.data.payload?.invitee?.uri;
      if (booking?.booking_id && eventUri && inviteeUri) {
        api.post(`/demo-bookings/${booking.booking_id}/confirm`, { event_uri: eventUri, invitee_uri: inviteeUri })
          .then(res => setConfirmedMeeting(res.data))
          .catch(() => {}); // best-effort — booking still succeeded either way
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [booking]);

  const submit = async () => {
    if (!form.name.trim()) { setError("Please enter your name"); return; }
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) { setError("Please enter a valid email"); return; }
    if (!form.company.trim()) { setError("Please enter your company name"); return; }
    setError("");
    setBusy(true);
    try {
      const res = await api.post("/demo-bookings", form);
      setBooking(res.data);
      setStep(2);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong — please try again");
    } finally {
      setBusy(false);
    }
  };

  const modal = (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)" }}
      />

      {/* Modal */}
      <div className="relative w-full bg-white border border-gray-100 rounded-2xl shadow-2xl overflow-y-auto" style={{ maxWidth: step === 2 ? 680 : 520, maxHeight: "90vh" }}>
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-violet-400 via-violet-600 to-violet-400" />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 p-1 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {step === 1 ? (
          <div className="p-8">
            <div className="mb-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-violet-100 border border-violet-200 rounded-full text-violet-600 text-[11px] font-semibold mb-3">
                <Calendar className="w-3 h-3" /> Book a personalised demo
              </span>
              <h2 className="text-[22px] font-bold text-gray-900 mb-1">See CombinedWork in action</h2>
              <p className="text-gray-500 text-[13px] m-0">30 minutes · Live walkthrough · Tailored to your team</p>
            </div>

            <div className="flex flex-col gap-4">
              {/* Name + Email */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Your name *</label>
                  <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Jane Smith" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Work email *</label>
                  <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="jane@company.com" className={inputClass} />
                </div>
              </div>

              {/* Company */}
              <div>
                <label className={labelClass}>Company *</label>
                <input value={form.company} onChange={e => set("company", e.target.value)} placeholder="Acme Ltd" className={inputClass} />
              </div>

              {/* Size + Role */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Team size</label>
                  <select value={form.company_size} onChange={e => set("company_size", e.target.value)} className={`${inputClass} appearance-none`}>
                    <option value="">Select…</option>
                    {SIZES.map(s => <option key={s} value={s}>{s} people</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Your role</label>
                  <select value={form.role} onChange={e => set("role", e.target.value)} className={`${inputClass} appearance-none`}>
                    <option value="">Select…</option>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className={labelClass}>Anything you'd like us to cover? (optional)</label>
                <textarea value={form.message} onChange={e => set("message", e.target.value)} rows={2}
                  placeholder="e.g. We run agile sprints and need better invoicing…"
                  className="w-full bg-white border border-gray-200 text-gray-900 text-sm px-3 py-2 rounded-lg outline-none resize-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-colors" />
              </div>

              {error && <p className="text-red-500 text-xs m-0">{error}</p>}

              <button onClick={submit} disabled={busy}
                className={`w-full h-12 rounded-full text-white font-bold text-sm border-none flex items-center justify-center gap-2 transition-all shadow-md shadow-violet-200 ${
                  busy ? "bg-violet-400 cursor-not-allowed" : "bg-violet-600 hover:bg-violet-700 cursor-pointer"
                }`}>
                {busy
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <>Choose a time <ArrowRight className="w-4 h-4" /></>}
              </button>

              <p className="text-center text-[11px] text-gray-400 m-0">Next: pick a slot on our calendar — you'll get an instant confirmation with a video call link.</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            {calendlyConfirmed ? (
              <div className="px-8 py-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-100 border border-violet-200 mb-6">
                  <CheckCircle2 className="w-8 h-8 text-violet-600" />
                </div>
                <h2 className="text-[22px] font-bold text-gray-900 mb-2">You're booked!</h2>
                <p className="text-gray-500 mb-2">
                  Thanks, <strong className="text-gray-900">{form.name}</strong>. Calendly just sent a calendar
                  invite with your video call link to <strong className="text-gray-900">{form.email}</strong>.
                </p>
                {confirmedMeeting?.meeting_url && (
                  <a href={confirmedMeeting.meeting_url} target="_blank" rel="noreferrer"
                    className="inline-block text-violet-600 text-sm font-semibold no-underline mb-8 hover:text-violet-700 break-all">
                    {confirmedMeeting.meeting_url}
                  </a>
                )}
                {!confirmedMeeting?.meeting_url && <div className="mb-8" />}
                <div className="flex flex-col gap-3">
                  <a href="/auth?mode=signup"
                    className="flex w-full h-11 items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 rounded-full text-white font-bold text-sm no-underline transition-colors shadow-md shadow-violet-200">
                    Start for free in the meantime <ArrowRight className="w-4 h-4" />
                  </a>
                  <button onClick={onClose}
                    className="w-full bg-transparent border-none text-gray-500 text-sm cursor-pointer p-2 hover:text-gray-700">
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="px-6 pt-6 pb-2">
                  <h2 className="text-lg font-bold text-gray-900 mb-1">Pick a time</h2>
                  <p className="text-gray-500 text-[13px] m-0">Choose a slot that works for you — we'll take it from here.</p>
                </div>
                {booking?.scheduling_url ? (
                  <div
                    className="calendly-inline-widget"
                    data-url={booking.scheduling_url}
                    style={{ minWidth: 320, height: 620 }}
                  />
                ) : (
                  <div className="px-8 py-12 text-center text-gray-500 text-sm">
                    Thanks — we've got your details and will reach out within one business day to find a time.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
